import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { API, setToken, formatApiError } from "@/lib/api";

const inputClass =
  "w-full rounded-none border border-seam bg-pit px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-acid";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setToken(data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed. Check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-ink px-6">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="w-full max-w-md">
        <Link to="/" data-testid="admin-login-logo" className="font-display text-3xl font-black italic uppercase tracking-tighter">
          Take The <span className="text-acid">Leap</span>
        </Link>
        <p className="mt-2 text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Owner access</p>

        <form data-testid="admin-login-form" onSubmit={onSubmit} className="mt-8 border border-seam bg-coal p-8">
          <label htmlFor="admin-email" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@taketheleap.com"
            className={inputClass}
          />
          <label htmlFor="admin-password" className="mb-2 mt-6 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={inputClass}
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-5 border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="group mt-8 flex w-full items-center justify-center gap-2 bg-acid px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-black transition-colors duration-200 hover:bg-white disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
            {!loading && <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
          </button>
        </form>
        <Link to="/" data-testid="admin-login-back-link" className="mt-6 inline-block text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 transition-colors duration-200 hover:text-acid">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}
